"use client";

import { Controller, UseFormReturn } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import {
  Field,
  FieldLabel,
  FieldError,
  FieldGroup,
} from "@/components/ui/field";

interface OtpStepProps {
  form: UseFormReturn<{ otp: string }>;
  email: string;
  isSubmitting: boolean;
  countdown: number;
  onSubmit: (data: { otp: string }) => void;
  onBack: () => void;
  onResend: () => void;
}

export function OtpStep({
  form,
  email,
  isSubmitting,
  countdown,
  onSubmit,
  onBack,
  onResend,
}: OtpStepProps) {
  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      className="space-y-6"
      id="otp-form"
    >
      <FieldGroup>
        <Field>
          <FieldLabel htmlFor="email-display">Email</FieldLabel>
          <Input id="email-display" type="email" value={email} disabled />
        </Field>

        <Controller
          name="otp"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid} className="gap-2">
              <FieldLabel>Código de verificación</FieldLabel>
              <div className="flex justify-center pt-1">
                <InputOTP
                  containerClassName="mx-auto w-fit"
                  maxLength={6}
                  value={field.value}
                  onChange={(value: string) => {
                    const sanitizedValue = value.replace(/\D/g, "");
                    field.onChange(sanitizedValue);
                    if (sanitizedValue.length === 6 && !isSubmitting) {
                      setTimeout(() => {
                        const form = document.getElementById(
                          "otp-form",
                        ) as HTMLFormElement;
                        form?.requestSubmit();
                      }, 100);
                    }
                  }}
                  onPaste={(e: React.ClipboardEvent) => {
                    e.preventDefault();
                    const pastedData = e.clipboardData.getData("text");
                    const sanitizedValue = pastedData
                      .replace(/\D/g, "")
                      .slice(0, 6);
                    field.onChange(sanitizedValue);
                    if (sanitizedValue.length === 6 && !isSubmitting) {
                      setTimeout(() => {
                        const form = document.getElementById(
                          "otp-form",
                        ) as HTMLFormElement;
                        form?.requestSubmit();
                      }, 100);
                    }
                  }}
                  pattern="^[0-9]*$"
                  disabled={isSubmitting}
                >
                  <InputOTPGroup className="gap-1 sm:gap-2">
                    <InputOTPSlot
                      index={0}
                      className="h-10 w-10 text-lg sm:h-12 sm:w-12 sm:text-xl"
                    />
                    <InputOTPSlot
                      index={1}
                      className="h-10 w-10 text-lg sm:h-12 sm:w-12 sm:text-xl"
                    />
                    <InputOTPSlot
                      index={2}
                      className="h-10 w-10 text-lg sm:h-12 sm:w-12 sm:text-xl"
                    />
                  </InputOTPGroup>
                  <InputOTPSeparator />
                  <InputOTPGroup className="gap-1 sm:gap-2">
                    <InputOTPSlot
                      index={3}
                      className="h-10 w-10 text-lg sm:h-12 sm:w-12 sm:text-xl"
                    />
                    <InputOTPSlot
                      index={4}
                      className="h-10 w-10 text-lg sm:h-12 sm:w-12 sm:text-xl"
                    />
                    <InputOTPSlot
                      index={5}
                      className="h-10 w-10 text-lg sm:h-12 sm:w-12 sm:text-xl"
                    />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <p className="text-muted-foreground mx-auto max-w-xs pt-1 text-center text-xs leading-relaxed text-balance">
                Si no lo ves, revisa spam o promociones. El código es válido por
                10 minutos.
              </p>
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />
      </FieldGroup>

      <div className="flex items-center justify-between">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onBack}
          disabled={isSubmitting}
        >
          Volver
        </Button>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onResend}
          disabled={countdown > 0 || isSubmitting}
        >
          {countdown > 0 ? `Reenviar en ${countdown}s` : "Reenviar código"}
        </Button>
      </div>

      <Button
        type="submit"
        className="w-full"
        disabled={isSubmitting || form.watch("otp").length !== 6}
      >
        {isSubmitting ? "Verificando..." : "Verificar código"}
      </Button>
    </form>
  );
}
